import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import Layout from "../../components/Layout";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Shield, UserPlus, Trash2, Edit, Save, X, ChevronDown, ChevronRight, History, Mail, Sparkles, AlertTriangle, Activity, DollarSign, Trophy, Tag, Disc, MessageSquare, Users, HardDrive, ClipboardList } from "lucide-react";

const ROLE_LABELS = {
  owner: "Eier",
  admin: "Admin",
  staff: "Staff",
  user: "Bruker",
};

const ROLE_COLORS = {
  owner: "text-[var(--magenta)]",
  admin: "text-amber-400",
  staff: "text-cyan-400",
};

function EditMemberPanel({ member, allPerms, presets, onClose, onSaved, isOwner }) {
  const [perms, setPerms] = useState(new Set(member.permissions || []));
  const [title, setTitle] = useState(member.staff_title || "");
  const [role, setRole] = useState(member.role || "staff");
  const [busy, setBusy] = useState(false);

  const togglePerm = (key) => {
    const next = new Set(perms);
    if (next.has(key)) next.delete(key); else next.add(key);
    setPerms(next);
  };

  const applyPreset = (name) => {
    const presetPerms = presets[name] || [];
    setPerms(new Set([...perms, ...presetPerms]));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/team/${member.id}`, {
        permissions: [...perms].sort(),
        staff_title: title,
        role,
      });
      toast.success("Lagret");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Fjerne ${member.username} fra staff?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/team/${member.id}`);
      toast.success("Fjernet fra staff");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke fjerne");
    } finally {
      setBusy(false);
    }
  };

  // Grupper permissions for visning
  const groups = {};
  for (const p of allPerms) {
    const grp = p.key.split(".")[0];
    if (!groups[grp]) groups[grp] = [];
    groups[grp].push(p);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-member-panel">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0F0F0F] border border-[var(--magenta)]/30 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">@{member.username}</h2>
            <p className="text-xs text-[var(--cream-soft)] mt-1">Rediger rolle og tilganger</p>
          </div>
          <button onClick={onClose} className="text-[var(--cream-soft)] hover:text-[var(--cream)]"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">STILLINGSBETEGNELSE</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="f.eks. Senior Moderator"
              maxLength={60}
              className="w-full bg-[var(--noir-2)] border border-white/15 px-3 py-2 text-sm text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
              data-testid="member-title-input"
            />
          </div>

          <div>
            <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">ROLLE</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full bg-[var(--noir-2)] border border-white/15 px-3 py-2 text-sm text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
              data-testid="member-role-select"
            >
              <option value="staff">Staff (kun eksplisitte permissions)</option>
              {isOwner && <option value="admin">Admin (alle permissions unntatt system.*)</option>}
            </select>
            <p className="text-[10px] text-[var(--cream-faint)] mt-1">
              {role === "admin" ? "Admin har automatisk ALLE permissions unntatt system.*. Kun eier kan tildele dette." : "Staff har KUN permissions du krysser av nedenfor."}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)]">PRESET-ROLLER (legger til på eksisterende)</label>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.keys(presets).map((name) => (
                <button
                  key={name}
                  onClick={() => applyPreset(name)}
                  className="px-3 py-1 text-xs font-meta tracking-[0.15em] border border-white/15 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
                  data-testid={`preset-${name}`}
                  type="button"
                >
                  + {name.toUpperCase().replace("_", " ")}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">
              GRANULÆRE TILGANGER ({perms.size}/{allPerms.length})
            </label>
            <div className="space-y-3" data-testid="permissions-grid">
              {Object.entries(groups).map(([grp, items]) => (
                <div key={grp} className="border border-white/5 p-3">
                  <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-2">{grp.toUpperCase()}</div>
                  <div className="space-y-1.5">
                    {items.map((p) => {
                      const isSystem = p.key.startsWith("system.");
                      return (
                        <label key={p.key} className={`flex items-start gap-2 text-sm cursor-pointer ${isSystem && !isOwner ? "opacity-40" : ""}`}>
                          <input
                            type="checkbox"
                            checked={perms.has(p.key)}
                            onChange={() => togglePerm(p.key)}
                            disabled={isSystem && !isOwner}
                            className="mt-0.5 accent-[var(--magenta)]"
                            data-testid={`perm-${p.key}`}
                          />
                          <div className="flex-1">
                            <div className="text-[var(--cream)]">{p.label}</div>
                            <div className="text-[10px] text-[var(--cream-faint)] font-mono">{p.key}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-white/10 flex items-center justify-between sticky bottom-0 bg-[#0F0F0F]">
          <button
            onClick={remove}
            disabled={busy}
            className="text-xs font-meta tracking-[0.15em] text-red-400 hover:text-red-300 disabled:opacity-40"
            data-testid="remove-member-btn"
          >
            <Trash2 className="h-3 w-3 inline mr-1" /> FJERN FRA STAFF
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-xs font-meta tracking-[0.15em] border border-white/20 text-[var(--cream-soft)] hover:border-[var(--cream)]">AVBRYT</button>
            <button onClick={save} disabled={busy} className="px-4 py-2 text-xs font-meta tracking-[0.15em] bg-[var(--magenta)] text-white hover:opacity-90 disabled:opacity-40" data-testid="save-member-btn">
              <Save className="h-3 w-3 inline mr-1" /> LAGRE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddMemberDialog({ onClose, onAdded, presets }) {
  const [username, setUsername] = useState("");
  const [staffTitle, setStaffTitle] = useState("");
  const [preset, setPreset] = useState("forum_moderator");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!username.trim()) { toast.error("Skriv inn brukernavn"); return; }
    setBusy(true);
    try {
      await api.post("/admin/team", {
        username: username.trim(),
        role: "staff",
        permissions: presets[preset] || [],
        staff_title: staffTitle || preset.replace("_", " "),
      });
      toast.success(`Lagt til som ${preset.replace("_", " ")}`);
      onAdded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke legge til");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="add-member-dialog">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0F0F0F] border border-[var(--magenta)]/30 max-w-md w-full">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Legg til staff</h2>
          <button onClick={onClose} className="text-[var(--cream-soft)] hover:text-[var(--cream)]"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">BRUKERNAVN</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="f.eks. Sofie"
              className="w-full bg-[var(--noir-2)] border border-white/15 px-3 py-2 text-sm text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
              data-testid="add-username-input" />
          </div>
          <div>
            <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">STILLINGSBETEGNELSE</label>
            <input value={staffTitle} onChange={(e) => setStaffTitle(e.target.value)} placeholder="f.eks. Senior Moderator"
              className="w-full bg-[var(--noir-2)] border border-white/15 px-3 py-2 text-sm text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
              data-testid="add-title-input" />
          </div>
          <div>
            <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">STARTROLLE-PRESET</label>
            <select value={preset} onChange={(e) => setPreset(e.target.value)}
              className="w-full bg-[var(--noir-2)] border border-white/15 px-3 py-2 text-sm text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
              data-testid="add-preset-select">
              {Object.keys(presets).map((name) => (
                <option key={name} value={name}>{name.replace("_", " ").toUpperCase()} ({presets[name].length} permissions)</option>
              ))}
            </select>
            <p className="text-[10px] text-[var(--cream-faint)] mt-1">Du kan finjustere tilgangene etter at brukeren er lagt til.</p>
          </div>
        </div>
        <div className="p-6 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-meta tracking-[0.15em] border border-white/20 text-[var(--cream-soft)] hover:border-[var(--cream)]">AVBRYT</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 text-xs font-meta tracking-[0.15em] bg-[var(--magenta)] text-white hover:opacity-90 disabled:opacity-40" data-testid="add-submit-btn">
            {busy ? "..." : "LEGG TIL"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBoard() {
  const { user: me } = useAuth();
  const [team, setTeam] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [presets, setPresets] = useState({});
  const [auditLog, setAuditLog] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [showTestEmail, setShowTestEmail] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testEmailBusy, setTestEmailBusy] = useState(null);
  const [autoApproval, setAutoApproval] = useState(null);
  const [autoUntilInput, setAutoUntilInput] = useState("");
  const [autoBusy, setAutoBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [usersOverview, setUsersOverview] = useState(null);
  const [usersOverviewBusy, setUsersOverviewBusy] = useState(false);
  const [promoAudit, setPromoAudit] = useState(null);
  const [promoAuditBusy, setPromoAuditBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/permissions").then((r) => r.data),
      api.get("/admin/team").then((r) => r.data),
      api.get("/admin/creator-auto-approval").then((r) => r.data).catch(() => null),
    ]).then(([permsData, teamData, autoData]) => {
      setAllPerms(permsData.permissions || []);
      setPresets(permsData.presets || {});
      setTeam(teamData.team || []);
      if (autoData) {
        setAutoApproval(autoData);
        // For datetime-local input
        if (autoData.until) {
          const d = new Date(autoData.until);
          const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
            .toISOString().slice(0, 16);
          setAutoUntilInput(local);
        }
      }
    }).catch((e) => {
      toast.error(e.response?.data?.detail || "Kunne ikke laste admin-board");
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const loadAudit = () => {
    api.get("/admin/audit-log").then((r) => setAuditLog(r.data.log || [])).catch(() => {});
  };

  useEffect(() => { if (showAudit) loadAudit(); }, [showAudit]);

  const sendTestEmail = async (kind) => {
    setTestEmailBusy(kind);
    try {
      const { data } = await api.post("/admin/test-email", {
        kind,
        to_email: testEmailTo.trim() || undefined,
      });
      toast.success(`E-post sendt til ${data.to} (id: ${data.email_id?.slice(0, 8)}…)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke sende test-e-post");
    } finally {
      setTestEmailBusy(null);
    }
  };

  const saveAutoApproval = async (enabled) => {
    setAutoBusy(true);
    try {
      let untilIso = null;
      if (enabled) {
        if (!autoUntilInput) {
          toast.error("Velg en lanseringsdato først");
          setAutoBusy(false);
          return;
        }
        untilIso = new Date(autoUntilInput).toISOString();
      }
      const { data } = await api.put("/admin/creator-auto-approval", {
        enabled,
        until: untilIso,
      });
      setAutoApproval(data);
      toast.success(enabled ? "Auto-godkjenning aktivert" : "Auto-godkjenning slått av");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setAutoBusy(false);
    }
  };


  const loadUsersOverview = async () => {
    setUsersOverviewBusy(true);
    try {
      const { data } = await api.get("/admin/users-overview");
      setUsersOverview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke hente brukerliste");
    } finally {
      setUsersOverviewBusy(false);
    }
  };

  const loadPromoAudit = async () => {
    setPromoAuditBusy(true);
    try {
      const { data } = await api.get("/admin/promo-codes-audit");
      setPromoAudit(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke hente kode-audit");
    } finally {
      setPromoAuditBusy(false);
    }
  };




  if (loading) {
    return <Layout><div className="text-center text-[var(--cream-soft)] py-20">Laster admin-board…</div></Layout>;
  }

  const isOwner = me?.role === "owner";

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="admin-board-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--magenta)] mb-1 flex items-center gap-2">
              <Shield className="h-3 w-3" /> ADMIN BOARD
            </div>
            <h1 className="font-fraunces italic text-4xl text-[var(--cream)]">Staff & tilganger</h1>
            <p className="text-sm text-[var(--cream-soft)] mt-2">
              Tildel granulære permissions til moderatorer og admins. Eieren har alle tilganger og kan ikke fjernes.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowTestEmail(!showTestEmail)}
              className="px-3 py-2 text-xs font-meta tracking-[0.15em] border border-white/20 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
              data-testid="test-email-toggle">
              <Mail className="h-3 w-3 inline mr-1" /> {showTestEmail ? "SKJUL TEST-E-POST" : "TEST E-POST"}
            </button>
            <button onClick={() => setShowAudit(!showAudit)}
              className="px-3 py-2 text-xs font-meta tracking-[0.15em] border border-white/20 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
              data-testid="audit-toggle">
              <History className="h-3 w-3 inline mr-1" /> {showAudit ? "SKJUL LOGG" : "AUDIT LOG"}
            </button>
            <button onClick={() => setShowAddDialog(true)}
              className="px-4 py-2 text-xs font-meta tracking-[0.15em] bg-[var(--magenta)] text-white hover:opacity-90"
              data-testid="add-member-btn">
              <UserPlus className="h-3 w-3 inline mr-1" /> LEGG TIL STAFF
            </button>
          </div>
        </div>

        {/* Admin-verktøy – snarveier til alle admin-sider */}
        <div className="mb-8" data-testid="admin-tools-grid">
          <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--cream-soft)] mb-3">ADMIN-VERKTØY</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[
              { to: "/admin/helse",            icon: Activity,      label: "Helse",              ownerOnly: false },
              { to: "/admin/okonomi",          icon: DollarSign,    label: "Økonomi",            ownerOnly: false },
              { to: "/konkurranser",           icon: Trophy,        label: "Konkurranser",       ownerOnly: false },
              { to: "/admin/kampanjer",        icon: Tag,           label: "Kampanjer & verving",ownerOnly: false },
              { to: "/admin/avstemnings-hjul", icon: Disc,          label: "Avstemmingshjul",    ownerOnly: false },
              { to: "/admin/forum",            icon: MessageSquare, label: "Forum",              ownerOnly: false },
              { to: "/admin/aktivitet",        icon: Users,         label: "Aktivitet",          ownerOnly: true  },
              { to: "/admin/media-diagnose",   icon: HardDrive,     label: "Media-diagnose",     ownerOnly: true  },
              { to: "/admin/gamle-album-filer", icon: HardDrive,   label: "Gamle album-filer",  ownerOnly: true  },
              { to: "/admin/epost-log",        icon: Mail,          label: "E-post-log",         ownerOnly: true  },
              { to: "/admin/skjema",           icon: ClipboardList, label: "Skjema",             ownerOnly: true  },
              { to: "/admin/brukere",          icon: Users,         label: "Brukere",            ownerOnly: false },
              { to: "/admin/promo-innboks",    icon: MessageSquare, label: "Promo-innboks",      ownerOnly: true  },
            ]
              .filter((it) => !it.ownerOnly || me?.role === "owner")
              .map((it) => (
                <Link
                  key={it.to}
                  to={it.to}
                  className="panel-tight p-4 flex items-center gap-3 border border-white/10 hover:border-[var(--magenta)] transition group"
                  data-testid={`admin-tool-${it.to.replace(/\//g, "-").slice(1)}`}
                >
                  <it.icon className="h-4 w-4 text-[var(--magenta)] shrink-0 group-hover:scale-110 transition" />
                  <span className="font-meta text-xs uppercase tracking-widest text-[var(--cream)] group-hover:text-[var(--magenta)]">
                    {it.label}
                  </span>
                </Link>
              ))}
          </div>
        </div>

        {/* Test-e-post-panel */}
        {showTestEmail && (
          <div className="panel p-5 mb-6" data-testid="test-email-panel">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-1">
                  RESEND – TRANSAKSJONS-E-POST
                </div>
                <h3 className="font-fraunces italic text-xl text-[var(--cream)]">
                  Send test-utgave av e-postmalene
                </h3>
                <p className="text-xs text-[var(--cream-soft)] mt-2 leading-relaxed max-w-xl">
                  Resend er i sandbox-modus inntil norskerotikk.no DNS er konfigurert.
                  Det betyr at e-posten kun kommer fram til adressen du registrerte
                  Resend-kontoen med. La feltet stå tomt for å sende til din egen
                  admin-e-post.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
              <div className="flex-1">
                <label className="block text-[10px] font-meta tracking-[0.2em] text-[var(--cream-soft)] mb-2">
                  MOTTAKER (VALGFRITT)
                </label>
                <input
                  type="email"
                  value={testEmailTo}
                  onChange={(e) => setTestEmailTo(e.target.value)}
                  placeholder={me?.email || "din@email.no"}
                  className="w-full bg-[var(--noir)] border border-white/10 px-3 py-2 text-sm text-[var(--cream)] focus:border-[var(--magenta)] focus:outline-none"
                  data-testid="test-email-recipient"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => sendTestEmail("trial_ending")}
                  disabled={!!testEmailBusy}
                  className="px-4 py-2 text-xs font-meta tracking-[0.15em] border border-[var(--magenta)] text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-white disabled:opacity-40 transition-colors"
                  data-testid="test-email-trial-btn"
                >
                  {testEmailBusy === "trial_ending" ? "SENDER…" : "TRIAL UTLØPER"}
                </button>
                <button
                  onClick={() => sendTestEmail("wheel_winner")}
                  disabled={!!testEmailBusy}
                  className="px-4 py-2 text-xs font-meta tracking-[0.15em] border border-[var(--magenta)] text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-white disabled:opacity-40 transition-colors"
                  data-testid="test-email-wheel-btn"
                >
                  {testEmailBusy === "wheel_winner" ? "SENDER…" : "LYKKEHJUL-VINST"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Promo-kode-audit – ALLE koder + bruk-status + diagnose */}
        <div className="panel p-5 mb-6 border-l-2 border-purple-500" data-testid="promo-audit-panel">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="font-meta text-[10px] tracking-[0.2em] text-purple-400 mb-1">DIAGNOSE</div>
              <h3 className="font-fraunces italic text-xl text-[var(--cream)]">
                Promo-kode-audit
              </h3>
              <p className="text-xs text-[var(--cream-soft)] mt-2 leading-relaxed max-w-2xl">
                Viser ALLE promo-koder + om de er brukt, av hvem, og om brukeren
                faktisk fikk forventet status (is_creator / VIP). Hvis en kode er
                markert «brukt» men brukeren mangler creator-status er det
                program-feil – ikke menneskelig feil.
              </p>
            </div>
            <button
              onClick={loadPromoAudit}
              disabled={promoAuditBusy}
              className="px-4 py-2 text-xs font-meta tracking-[0.15em] border border-purple-500/40 text-purple-400 hover:bg-purple-500/10 disabled:opacity-40"
              data-testid="promo-audit-btn"
            >
              {promoAuditBusy ? "..." : "HENT KODE-AUDIT"}
            </button>
          </div>

          {promoAudit && (
            <div className="mt-3 text-xs font-mono bg-[var(--noir)] border border-white/10 max-h-[500px] overflow-auto" data-testid="promo-audit-output">
              <table className="w-full">
                <thead className="sticky top-0 bg-[var(--noir)] border-b border-white/15">
                  <tr className="text-left text-[var(--cream-soft)] uppercase text-[10px] tracking-wider">
                    <th className="px-3 py-2">Kode</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Dager</th>
                    <th className="px-3 py-2">Brukt?</th>
                    <th className="px-3 py-2">Brukt av</th>
                    <th className="px-3 py-2">→ is_creator</th>
                    <th className="px-3 py-2">→ VIP</th>
                    <th className="px-3 py-2">Diagnose</th>
                    <th className="px-3 py-2">Brukt dato</th>
                  </tr>
                </thead>
                <tbody>
                  {(promoAudit.results || []).map((c) => {
                    const status = c.actual_status;
                    let diagnosis = "—";
                    let diagClass = "text-[var(--cream-faint)]";
                    if (c.used) {
                      if (!status) {
                        diagnosis = "BUG: brukt men ingen status";
                        diagClass = "text-red-400";
                      } else if (c.kind === "vip_creator_combo" && !status.is_creator) {
                        diagnosis = "BUG: skulle vært creator";
                        diagClass = "text-red-400";
                      } else if ((c.kind === "vip" || c.kind === "vip_creator_combo") && !status.has_vip) {
                        diagnosis = "BUG: skulle vært VIP";
                        diagClass = "text-red-400";
                      } else {
                        diagnosis = "OK";
                        diagClass = "text-emerald-400";
                      }
                    } else {
                      diagnosis = "Ikke brukt";
                    }
                    return (
                      <tr key={c.code} className="border-b border-white/5 hover:bg-white/3">
                        <td className="px-3 py-2 text-[var(--magenta)] font-mono">{c.code}</td>
                        <td className="px-3 py-2 text-[var(--cream)]">{c.kind}</td>
                        <td className="px-3 py-2 text-[var(--cream-soft)]">{c.days}</td>
                        <td className="px-3 py-2">
                          {c.used ? <span className="text-emerald-400">✓</span> : <span className="text-[var(--cream-faint)]">—</span>}
                        </td>
                        <td className="px-3 py-2 text-[var(--cream)]">{status?.username || c.used_by_username || "—"}</td>
                        <td className="px-3 py-2">
                          {status?.is_creator ? <span className="text-emerald-400">✓</span> : <span className="text-[var(--cream-faint)]">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          {status?.has_vip ? <span className="text-[var(--gold)]">★</span> : <span className="text-[var(--cream-faint)]">—</span>}
                        </td>
                        <td className={`px-3 py-2 ${diagClass}`}>{diagnosis}</td>
                        <td className="px-3 py-2 text-[var(--cream-faint)] text-[10px]">
                          {c.used_at ? new Date(c.used_at).toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" }) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="px-3 py-2 text-[var(--cream-soft)] border-t border-white/10">
                Totalt: {promoAudit.total} koder
              </div>
            </div>
          )}
        </div>

        {/* Brukeroversikt – hvem ble creator, hvem brukte hvilken kode */}
        <div className="panel p-5 mb-6 border-l-2 border-cyan-500" data-testid="users-overview-panel">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="font-meta text-[10px] tracking-[0.2em] text-cyan-400 mb-1">DIAGNOSE</div>
              <h3 className="font-fraunces italic text-xl text-[var(--cream)]">
                Brukeroversikt – kode og creator-status
              </h3>
              <p className="text-xs text-[var(--cream-soft)] mt-2 leading-relaxed max-w-2xl">
                Viser hvilken promo-kode hver bruker brukte ved registrering, og om de
                ble creator. Bruk dette for å forstå hvorfor noen ikke ble creator
                selv om de fikk en kode.
              </p>
            </div>
            <button
              onClick={loadUsersOverview}
              disabled={usersOverviewBusy}
              className="px-4 py-2 text-xs font-meta tracking-[0.15em] border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10 disabled:opacity-40"
              data-testid="users-overview-btn"
            >
              {usersOverviewBusy ? "..." : "HENT LISTE"}
            </button>
          </div>

          {usersOverview && (
            <div className="mt-3 text-xs font-mono bg-[var(--noir)] border border-white/10 max-h-[500px] overflow-auto" data-testid="users-overview-output">
              <table className="w-full">
                <thead className="sticky top-0 bg-[var(--noir)] border-b border-white/15">
                  <tr className="text-left text-[var(--cream-soft)] uppercase text-[10px] tracking-wider">
                    <th className="px-3 py-2">Brukernavn</th>
                    <th className="px-3 py-2">E-post</th>
                    <th className="px-3 py-2">Creator?</th>
                    <th className="px-3 py-2">VIP</th>
                    <th className="px-3 py-2">E-post bekr.</th>
                    <th className="px-3 py-2">Kode brukt</th>
                    <th className="px-3 py-2">Kode-type</th>
                    <th className="px-3 py-2">Registrert</th>
                    <th className="px-3 py-2">Sist sett</th>
                  </tr>
                </thead>
                <tbody>
                  {(usersOverview.results || []).map((u) => {
                    const isCreator = u.is_creator;
                    const hasVip = u.tier === "vip" && (!u.tier_until || new Date(u.tier_until) > new Date());
                    const last = u.last_used_code;  // {source, code, kind, days?, vip_days?, discount_pct?, purchase_type?, used_at}
                    // Bygg kode-type-tekst
                    let codeTypeLabel = "—";
                    if (last) {
                      if (last.source === "promo") {
                        codeTypeLabel = `${last.kind || "promo"}${last.days ? ` (${last.days}d)` : ""}`;
                      } else if (last.source === "campaign") {
                        if (last.kind === "free_vip") {
                          codeTypeLabel = `kampanje · gratis VIP${last.vip_days ? ` (${last.vip_days}d)` : ""}`;
                        } else {
                          codeTypeLabel = `kampanje · rabatt${last.discount_pct ? ` ${last.discount_pct}%` : ""}${last.purchase_type ? ` på ${last.purchase_type}` : ""}`;
                        }
                      }
                    }
                    return (
                      <tr key={u.id} className="border-b border-white/5 hover:bg-white/3">
                        <td className="px-3 py-2 text-[var(--cream)]">
                          {u.username}
                          {u.role && u.role !== "user" && (
                            <span className="ml-1 text-[9px] text-amber-400">[{u.role}]</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-[var(--cream-soft)] text-[10px]">{u.email}</td>
                        <td className="px-3 py-2">
                          {isCreator ? <span className="text-emerald-400">✓ JA</span> : <span className="text-[var(--cream-faint)]">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          {hasVip ? <span className="text-[var(--gold)]">★</span> : <span className="text-[var(--cream-faint)]">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          {u.email_verified ? <span className="text-emerald-400">✓</span> : <span className="text-amber-400">○</span>}
                        </td>
                        <td className="px-3 py-2 text-[var(--magenta)]" title={last?.used_at ? new Date(last.used_at).toLocaleString("nb-NO") : ""}>
                          {last?.code || <span className="text-[var(--cream-faint)]">—</span>}
                        </td>
                        <td className="px-3 py-2 text-[var(--cream-soft)] text-[10px]">
                          {codeTypeLabel}
                        </td>
                        <td className="px-3 py-2 text-[var(--cream-faint)] text-[10px]">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString("nb-NO") : "—"}
                        </td>
                        <td className="px-3 py-2 text-[var(--cream-faint)] text-[10px]">
                          {u.last_seen ? new Date(u.last_seen).toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" }) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="px-3 py-2 text-[var(--cream-soft)] border-t border-white/10">
                Totalt: {usersOverview.total} brukere
              </div>
            </div>
          )}
        </div>

        {/* Auto-godkjennings-vindu (launch-test) */}
        <div className="panel p-5 mb-6" data-testid="auto-approval-panel">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-1 flex items-center gap-2">
                <Sparkles className="h-3 w-3" /> LAUNCH-VINDU
              </div>
              <h3 className="font-fraunces italic text-xl text-[var(--cream)]">
                Auto-godkjenning av skaper-søknader
              </h3>
              <p className="text-xs text-[var(--cream-soft)] mt-2 leading-relaxed max-w-2xl">
                Når dette er aktivt blir ALLE som søker som skaper godkjent
                umiddelbart – uten krav til OnlyFans-lenke eller manuell behandling.
                Prøveperioden løper til lanseringsdatoen du setter, og deretter
                må de oppgradere. Brukes for å la testere utforske skaper-verktøyene
                før lansering.
              </p>
              {autoApproval && (
                <div className="flex flex-wrap gap-3 mt-3 text-xs">
                  <span className={`px-2 py-1 font-meta tracking-[0.15em] ${
                    autoApproval.is_active
                      ? "bg-[var(--magenta)] text-white"
                      : "border border-white/15 text-[var(--cream-faint)]"
                  }`}>
                    {autoApproval.is_active ? "AKTIV" : "AV"}
                  </span>
                  <span className="text-[var(--cream-soft)]">
                    Auto-godkjent så langt: <strong className="text-[var(--cream)]">{autoApproval.auto_approved_count}</strong>
                  </span>
                  {autoApproval.until && (
                    <span className="text-[var(--cream-soft)]">
                      Utløper: <strong className="text-[var(--cream)]">
                        {new Date(autoApproval.until).toLocaleString("nb-NO", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </strong>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
            <div className="flex-1">
              <label className="block text-[10px] font-meta tracking-[0.2em] text-[var(--cream-soft)] mb-2">
                LANSERINGSDATO (UTC)
              </label>
              <input
                type="datetime-local"
                value={autoUntilInput}
                onChange={(e) => setAutoUntilInput(e.target.value)}
                className="w-full bg-[var(--noir)] border border-white/10 px-3 py-2 text-sm text-[var(--cream)] focus:border-[var(--magenta)] focus:outline-none"
                data-testid="auto-until-input"
              />
            </div>
            <div className="flex gap-2">
              {autoApproval?.is_active ? (
                <button
                  onClick={() => saveAutoApproval(false)}
                  disabled={autoBusy}
                  className="px-4 py-2 text-xs font-meta tracking-[0.15em] border border-white/20 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)] disabled:opacity-40"
                  data-testid="auto-disable-btn"
                >
                  {autoBusy ? "..." : "SKRU AV"}
                </button>
              ) : null}
              <button
                onClick={() => saveAutoApproval(true)}
                disabled={autoBusy}
                className="px-4 py-2 text-xs font-meta tracking-[0.15em] bg-[var(--magenta)] text-white hover:opacity-90 disabled:opacity-40"
                data-testid="auto-save-btn"
              >
                {autoBusy ? "..." : (autoApproval?.is_active ? "OPPDATER DATO" : "AKTIVER")}
              </button>
            </div>
          </div>
        </div>

        {/* Audit log */}
        {showAudit && (
          <div className="panel p-5 mb-6" data-testid="audit-log">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-3">SISTE 100 HANDLINGER</div>
            {auditLog.length === 0 ? (
              <div className="text-xs text-[var(--cream-faint)]">Ingen logger ennå.</div>
            ) : (
              <div className="space-y-2 text-xs">
                {auditLog.map((entry) => (
                  <div key={entry.id} className="border-b border-white/5 pb-2 flex justify-between gap-3">
                    <div className="flex-1">
                      <span className="text-[var(--magenta)]">@{entry.actor_username}</span>
                      <span className="text-[var(--cream-soft)]"> {entry.action} </span>
                      {entry.target_id && <span className="text-[var(--cream-faint)] font-mono text-[10px]">{entry.target_id.slice(0, 8)}</span>}
                      {entry.meta && Object.keys(entry.meta).length > 0 && (
                        <div className="text-[10px] text-[var(--cream-faint)] mt-0.5 font-mono">{JSON.stringify(entry.meta)}</div>
                      )}
                    </div>
                    <div className="text-[10px] text-[var(--cream-faint)] whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Team-liste */}
        <div className="space-y-3" data-testid="team-list">
          {team.map((m) => {
            const isMe = m.id === me?.id;
            return (
              <div key={m.id} className="panel p-4 flex items-start gap-4" data-testid={`team-row-${m.username}`}>
                <div className="w-12 h-12 shrink-0 border-2 border-white/10 overflow-hidden bg-[var(--noir-2)]">
                  {m.avatar_url ? (
                    <img src={m.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-fraunces italic text-lg text-[var(--cream-soft)]">
                      {m.username.slice(0, 2).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/profile/${m.username}`} className="font-fraunces italic text-xl text-[var(--cream)] hover:text-[var(--magenta)]">
                      @{m.username}
                    </Link>
                    <span className={`text-xs font-meta tracking-[0.15em] ${ROLE_COLORS[m.role] || "text-[var(--cream-faint)]"}`}>
                      {(ROLE_LABELS[m.role] || m.role || "").toUpperCase()}
                    </span>
                    {m.is_protected && (
                      <span className="text-[10px] font-meta tracking-[0.15em] text-[var(--magenta)] border border-[var(--magenta)]/40 px-1.5 py-0.5">
                        PROTECTED
                      </span>
                    )}
                    {isMe && <span className="text-[10px] text-[var(--cream-faint)]">(du)</span>}
                  </div>
                  {m.staff_title && (
                    <div className="text-sm text-[var(--cream-soft)] mt-0.5">{m.staff_title}</div>
                  )}
                  <div className="text-xs text-[var(--cream-faint)] mt-2">
                    {m.effective_permissions.length} permissions
                    {m.role !== "owner" && m.permissions.length > 0 && (
                      <span> · {m.permissions.length} custom</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {!m.is_protected && m.role !== "owner" && (
                    <button onClick={() => setEditingMember(m)}
                      className="px-3 py-1.5 text-xs font-meta tracking-[0.15em] border border-white/20 text-[var(--cream)] hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
                      data-testid={`edit-${m.username}-btn`}>
                      <Edit className="h-3 w-3 inline mr-1" /> REDIGER
                    </button>
                  )}
                  {m.is_protected && (
                    <span className="text-[10px] text-[var(--cream-faint)]">Kan ikke endres</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {showAddDialog && (
          <AddMemberDialog
            onClose={() => setShowAddDialog(false)}
            onAdded={() => { setShowAddDialog(false); load(); }}
            presets={presets}
          />
        )}
        {editingMember && (
          <EditMemberPanel
            member={editingMember}
            allPerms={allPerms}
            presets={presets}
            isOwner={isOwner}
            onClose={() => setEditingMember(null)}
            onSaved={() => { setEditingMember(null); load(); }}
          />
        )}
      </div>
    </Layout>
  );
}
